import React, { useEffect, useState } from "react";
import { FaHandPointer } from "react-icons/fa6";
import { FiGlobe, FiMic, FiMonitor, FiPhone, FiVideo, FiX } from "react-icons/fi";
import SteffiDevice from "../components/SteffiDevice";
import PhoneMock, { useMockScreens } from "../components/PhoneMock";
import Reveal, { useInView } from "../components/Reveal";
import Figures from "../components/Figure";
import { APP_SHOTS, RENDERS, SCENES, useLocalizedAsset } from "../renders";
import { useSteffiCopy } from "../content";

const SPEC_ICONS = { display: FiMonitor, camera: FiVideo, press: FaHandPointer };
// what the family's camera shows once the call is picked up
const CALL_PHOTO = "/steffi/screens/call-family.webp";
const BEZEL_COLOR = "#0c0b0c";
// how far the round bezel reaches past the measured glass (see BezelRing)
const BEZEL_SCALE = 1.045;
// where the bezel starts, as a share of the glass radius (photo starts at 0.975)
const BEZEL_INNER = 0.97;

/* ringing → connected → idle, looping while the device composition is on screen */
const SCRIPT = [
  { state: "ringing", ms: 3600 },
  { state: "connected", ms: 4200 },
  { state: "idle", ms: 1800 },
];

function useCallCycle(active) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!active) {
      setStep(0);
      return undefined;
    }
    const t = setTimeout(() => setStep((s) => (s + 1) % SCRIPT.length), SCRIPT[step].ms);
    return () => clearTimeout(t);
  }, [active, step]);

  return SCRIPT[step].state;
}

function CallScreen({ state }) {
  const { CALL } = useSteffiCopy();
  const ringing = state === "ringing";
  const connected = state === "connected";

  return (
    <div className="relative h-full w-full">
      {/* incoming */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-[6%] px-[12%] text-center"
        style={{
          opacity: ringing ? 1 : 0,
          transform: `scale(${ringing ? 1 : 0.92})`,
          transition: "opacity 700ms cubic-bezier(.2,.8,.2,1), transform 700ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        <div className="relative">
          <span
            className="absolute inset-0 rounded-full border-2 border-[color:color-mix(in_srgb,var(--color-highlight)_60%,transparent)]"
            style={{ animation: ringing ? "steffi-ring 2.2s cubic-bezier(.2,.8,.2,1) infinite" : "none" }}
          />
          <span className="grid h-[3.2em] w-[3.2em] place-items-center rounded-full bg-[color:var(--color-primary)] text-[1.2em] font-semibold text-[color:var(--color-background)]">
            E
          </span>
        </div>
        <div className="leading-tight">
          <p className="text-[1.05em] font-semibold text-[color:var(--color-background)]">{CALL.states.ringing}</p>
          <p className="mt-[0.15em] text-[0.72em] text-white/60">{CALL.callerLine}</p>
        </div>
        <div className="flex items-center gap-[0.8em]">
          <span className="grid h-[1.9em] w-[1.9em] place-items-center rounded-full bg-[color:var(--color-highlight)] text-[color:var(--color-primary)]">
            <FiPhone className="h-[0.9em] w-[0.9em]" aria-hidden />
          </span>
          <span className="grid h-[1.9em] w-[1.9em] place-items-center rounded-full bg-[color:var(--color-primary)] text-[color:var(--color-background)]">
            <FiX className="h-[0.9em] w-[0.9em]" aria-hidden />
          </span>
        </div>
      </div>

      {/* connected: the family's camera picture on black glass, set in by an
          even band so it reads as the display's own bezel (see BezelRing). */}
      <div
        className="absolute inset-0"
        style={{
          opacity: connected ? 1 : 0,
          transition: "opacity 800ms cubic-bezier(.2,.8,.2,1)",
          background: BEZEL_COLOR,
        }}
      >
        <div
          className="absolute overflow-hidden rounded-full"
          style={{
            inset: "2.5%",
            transform: `scale(${connected ? 1 : 1.04})`,
            transition: "transform 900ms cubic-bezier(.2,.8,.2,1)",
          }}
        >
          <img
            src={CALL_PHOTO}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: "50% 38%" }}
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 46%, rgba(0,0,0,0) 58%, rgba(0,0,0,.42) 100%)," +
                "linear-gradient(to top, rgba(12,8,12,.6) 0%, rgba(12,8,12,0) 34%)," +
                "linear-gradient(140deg, rgba(255,255,255,.12) 0%, rgba(255,255,255,0) 32%)",
            }}
          />
          <p className="absolute inset-x-0 bottom-[10%] flex items-center justify-center gap-[0.5em] text-[0.72em] font-medium text-white">
            <span
              className="inline-block h-[0.5em] w-[0.5em] rounded-full bg-[color:var(--color-highlight)]"
              style={{ animation: "steffi-breathe 1.6s ease-in-out infinite" }}
            />
            {CALL.states.connected}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function CallSection() {
  const { CALL } = useSteffiCopy();
  const localize = useLocalizedAsset();
  const profileShot = localize(APP_SHOTS.profile);
  const [ref, inView] = useInView({ threshold: 0.3, once: false });
  const state = useCallCycle(inView);
  // Steffi's face waits until the call picture has faded, or both show at once.
  const [showFace, setShowFace] = useState(false);
  useEffect(() => {
    if (state !== "idle") {
      setShowFace(false);
      return undefined;
    }
    const t = setTimeout(() => setShowFace(true), 700);
    return () => clearTimeout(t);
  }, [state]);
  const mock = useMockScreens();

  return (
    <section
      id="anrufe"
      className="relative overflow-hidden py-28 lg:py-40"
      style={{ background: "var(--bg)" }}
    >
      <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-10">
        <div className="mx-auto max-w-[44rem] text-center">
          <Reveal as="h2" className="t-section">
            {CALL.title.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </Reveal>
          <Reveal as="p" delay={80} className="t-lead mt-6 text-[color:var(--ink-2)]">
            {CALL.subtitle}
          </Reveal>
        </div>

        {/* Both halves of a call in one frame: Steffi taking it, and the phone
            the family started it from. */}
        <div ref={ref} className="relative mt-14 lg:mt-20">
          <div
            aria-hidden
            className="glow-blob pointer-events-none absolute left-1/2 top-1/2 h-[46vh] w-[46vh] -translate-x-1/2 -translate-y-1/2"
            style={{
              background:
                "radial-gradient(circle, rgba(215,180,138,.42) 0%, rgba(248,242,236,0) 68%)",
              opacity: state === "idle" ? 0.6 : 1,
            }}
          />

          <div
            className="relative flex items-end justify-center gap-0"
            style={{ "--call-device-size": "clamp(14rem, 58vw, 38rem)" }}
          >
            <div className="relative" style={{ width: "var(--call-device-size)" }}>
              {/* Held still: the bezel ring and glow below sit outside the
                  device's own layer, so any float or lean would slide the
                  photo's glass out from under them. */}
              <SteffiDevice
                variant="calm"
                awake={showFace}
                followPointer={false}
                float={false}
                tilt={false}
                screen={<CallScreen state={state} />}
                alt={CALL.alts.call}
              />
              {/* The glass opening in this photograph is not a true circle — it
                  bulges ~5% further out at the lower corners. A slightly larger,
                  perfectly round black bezel covers that in every state, so the
                  rim looks the same while idle, ringing and connected. */}
              <div
                aria-hidden
                className="pointer-events-none absolute rounded-full"
                style={{
                  left: `${RENDERS.calm.face.cx}%`,
                  top: `${RENDERS.calm.face.cy}%`,
                  width: `${RENDERS.calm.face.rw * 2 * BEZEL_SCALE}%`,
                  height: `${RENDERS.calm.face.rh * 2 * BEZEL_SCALE}%`,
                  transform: "translate(-50%,-50%)",
                  // starts a little inside the glass: its edge is ~1% off-centre at the
                  // upper right, which otherwise leaves a hairline of housing
                  background: `radial-gradient(circle closest-side, transparent ${(100 * BEZEL_INNER) / BEZEL_SCALE - 0.4}%, ${BEZEL_COLOR} ${(100 * BEZEL_INNER) / BEZEL_SCALE}%)`,
                }}
              />
              {/* the ring the glass throws while the camera is live */}
              <div
                aria-hidden
                className="pointer-events-none absolute rounded-full"
                style={{
                  left: `${RENDERS.calm.face.cx}%`,
                  top: `${RENDERS.calm.face.cy}%`,
                  width: `${RENDERS.calm.face.rw * 2.5}%`,
                  height: `${RENDERS.calm.face.rh * 2.5}%`,
                  transform: "translate(-50%,-50%)",
                  background:
                    "radial-gradient(circle, rgba(250,189,94,.5) 0%, rgba(250,189,94,.16) 42%, rgba(250,189,94,0) 66%)",
                  opacity: state === "idle" ? 0 : 0.85,
                  transition: "opacity 900ms cubic-bezier(.2,.8,.2,1)",
                  filter: "blur(14px)",
                }}
              />
            </div>

            {mock ? (
              <PhoneMock
                screen="profile"
                alt={CALL.alts.app}
                className="shrink-0"
                style={{
                  width: "calc(var(--call-device-size) * 0.4)",
                  marginLeft: "calc(var(--call-device-size) * -0.16)",
                  marginBottom: "calc(var(--call-device-size) * 0.133)",
                }}
              />
            ) : (
            <img
              src={profileShot.src}
              width={profileShot.w}
              height={profileShot.h}
              alt={CALL.alts.app}
              loading="lazy"
              decoding="async"
              className="shrink-0"
              style={{
                width: "calc(var(--call-device-size) * 0.4)",
                filter: "drop-shadow(0 30px 56px rgba(36,33,30,.22))",
                marginLeft: "calc(var(--call-device-size) * -0.16)",
                marginBottom: "calc(var(--call-device-size) * 0.133)",
              }}
            />
            )}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-y-10 sm:grid-cols-3 sm:gap-y-0 lg:mt-20">
          {CALL.specs.map((spec, i) => {
            const Icon = SPEC_ICONS[spec.icon] || FiMonitor;
            return (
              <Reveal
                key={spec.title}
                delay={i * 110}
                className={
                  i > 0
                    ? "sm:border-l sm:border-[color:var(--border)] sm:pl-8 lg:pl-12"
                    : "sm:pr-8 lg:pr-12"
                }
              >
                <div className="flex items-start gap-5">
                  <span
                    aria-hidden
                    className="grid h-14 w-14 shrink-0 place-items-center rounded-full border border-[color:var(--border)] text-[color:var(--color-highlight)]"
                  >
                    <Icon className="h-6 w-6" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="ink-heading text-[clamp(1.125rem,1.5vw,1.75rem)] font-semibold tracking-[-0.01em]">
                      <Figures text={spec.title} mark={spec.mark} />
                    </h3>
                    <p className="mt-2.5 text-[clamp(1rem,1.2vw,1.35rem)] leading-relaxed text-[color:var(--ink-2)]">
                      {spec.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-24 lg:mt-36">
          <figure className="relative left-1/2 w-screen -translate-x-1/2 overflow-hidden">
            <img
              src={SCENES.call.src}
              width={SCENES.call.w}
              height={SCENES.call.h}
              alt={CALL.alts.scene}
              loading="lazy"
              decoding="async"
              className="block h-[72vh] max-h-[48rem] min-h-[28rem] w-full object-cover"
              style={{
                objectPosition: "66% 50%", /* keeps Steffi in view when narrow screens crop the sides */
                WebkitMaskImage:
                  "linear-gradient(90deg, transparent 0%, #000 7%, #000 93%, transparent 100%)",
                maskImage:
                  "linear-gradient(90deg, transparent 0%, #000 7%, #000 93%, transparent 100%)",
              }}
            />
          </figure>
        </Reveal>

        <HardwareNote />
      </div>
    </section>
  );
}

/* Spracherkennung. Two dry specs — ten languages, five metres — told the way
   the rest of the page talks: a quiet band, one fresh angle on the product, and
   the room's reach drawn as soft rings spreading out from her. */
function HardwareNote() {
  const { CALL } = useSteffiCopy();
  const featureIcons = { languages: FiGlobe, distance: FiMic };

  return (
    <div className="mt-24 lg:mt-36">
      <Reveal>
        <div className="hairline" />
      </Reveal>

      <div className="grid grid-cols-1 items-center gap-16 pt-16 lg:grid-cols-[1fr_0.72fr] lg:gap-20 lg:pt-20">
        <div>
          <Reveal as="p" className="t-eyebrow">
            {CALL.hardware.eyebrow}
          </Reveal>
          <Reveal as="h3" delay={80} className="t-section mt-6 !text-[clamp(1.875rem,3.6vw,2.875rem)]">
            {CALL.hardware.title.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-x-14 gap-y-10 sm:grid-cols-2">
            {CALL.hardware.features.map((feature, i) => {
              const Icon = featureIcons[feature.icon] || FiMic;
              return (
                <Reveal key={feature.highlight} delay={160 + i * 110}>
                  <div className="hairline" />
                  <div className="pt-6">
                    <span
                      aria-hidden
                      className="grid h-14 w-14 place-items-center rounded-full border border-[color:var(--border)] text-[color:var(--color-highlight)]"
                    >
                      <Icon className="h-6 w-6" />
                    </span>
                    <p className="mt-5 text-[clamp(0.9375rem,1.1vw,1.25rem)] text-[color:var(--ink-3)]">
                      {feature.lead}
                    </p>
                    <p
                      className="display ink-heading mt-2 text-[clamp(1.25rem,2vw,2.25rem)] leading-[1.12]"
                      style={{ textWrap: "nowrap", whiteSpace: "nowrap" }}
                    >
                      <Figures text={feature.highlight} mark={feature.mark} />
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Her voice pickup, drawn. */}
        <div className="relative mx-auto w-full max-w-[26rem]">
          <div aria-hidden className="pointer-events-none absolute inset-0 grid place-items-center">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="absolute aspect-square w-[72%] rounded-full border"
                style={{
                  borderColor: "var(--accent-soft)",
                  animation: `steffi-ring 6.5s cubic-bezier(.2,.8,.2,1) ${i * 2.15}s infinite`,
                }}
              />
            ))}
          </div>

          <div className="relative mx-auto w-[clamp(11rem,22vw,17rem)]">
            {/* three-quarter photo, cut out and framed 3:4, on a floor shadow */}
            <span aria-hidden className="steffi-ground" style={{ top: "95%", width: "62%" }} />
            <img
              src="/steffi/bot-34.webp"
              width="1000"
              height="1333"
              alt={CALL.alts.side}
              loading="lazy"
              decoding="async"
              className="product relative block h-auto w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
